import { useState, useEffect } from 'react';
import { useTenant } from '../../context/TenantContext'; 
import { createTenantContractor } from '../../services/crmService'; 
import { getCommissionRules } from '../../services/db'; // <-- ADDED: Fetching the Master Matrix
import { functions, db } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';

export default function ContractorIntakeForm({ onClose }) {
  const { tenantId, loading: tenantLoading } = useTenant(); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 'vault' (Internal FTL Flow) vs 'checkhq' (Automated Invite)
  const [onboardingMode, setOnboardingMode] = useState('vault');

  // Dynamic Locations State
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // --- ⚡️ NEW: DYNAMIC TIERS STATE ---
  const [commissionTiers, setCommissionTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '', 
    lastName: '', 
    corporateEmail: '', 
    personalEmail: '', 
    phone: '',
    operatingCity: '', 
    operatingState: '', 
    assignedTenant: '',
    commissionTier: '', // Starts empty, sets dynamically on load
    systemRole: 'ASSOCIATE', 
    notes: '',
    offlineW9: false,
    offlineICA: false,
    offlineNDA: false,
    offlineTechLicense: false,
    offlineAutonomy: false,
    offlineAzDibs: false
  });

  // --- FETCH ACTIVE SUB-LOCATIONS/DIVISIONS ---
  useEffect(() => {
    const fetchLocations = async () => {
      if (tenantLoading || !tenantId) return;
      
      setLoadingLocations(true);
      try {
        const storesRef = collection(db, 'clients', tenantId, 'stores');
        const q = query(storesRef, where('isActive', '==', true));
        const snapshot = await getDocs(q);
        
        const fetchedLocations = [];
        snapshot.forEach(doc => {
          fetchedLocations.push({ id: doc.id, ...doc.data() });
        });
        
        setLocations(fetchedLocations);
      } catch (error) {
        console.error("System Error: Failed to fetch operational locations.", error);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [tenantId, tenantLoading]);

  // --- ⚡️ NEW: FETCH MASTER RATE MATRIX TIERS ---
  useEffect(() => {
    const fetchTiers = async () => {
      if (tenantLoading || !tenantId) return;
      
      setLoadingTiers(true);
      try {
        const rules = await getCommissionRules(tenantId);
        
        if (rules && rules.tiers && rules.tiers.length > 0) {
          setCommissionTiers(rules.tiers);
          // Auto-select the first tier in the matrix
          setFormData(prev => ({ ...prev, commissionTier: rules.tiers[0].targetTier }));
        } else {
          // Fallback if the Master Agent hasn't set up their matrix yet
          const defaultTiers = [
            { id: '1', name: 'Standard Base (1099)', targetTier: 'Standard 1099' },
            { id: '2', name: 'Volume Accelerator (Gold)', targetTier: 'Gold 1099' },
            { id: '3', name: 'Master Rep (Platinum)', targetTier: 'Platinum 1099' }
          ];
          setCommissionTiers(defaultTiers);
          setFormData(prev => ({ ...prev, commissionTier: 'Standard 1099' }));
        }
      } catch (error) {
        console.error("System Error: Failed to fetch commission matrix.", error);
      } finally {
        setLoadingTiers(false);
      }
    };

    fetchTiers();
  }, [tenantId, tenantLoading]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tenantId) {
      alert("System Error: Corporate identity not resolved. Cannot provision personnel.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const matchedLocation = locations.find(loc => (loc.storeName || loc.name) === formData.assignedTenant);
      
      const derivedHomeStoreId = ['REGIONAL_DIRECTOR', 'MASTER_AGENT'].includes(formData.systemRole) 
        ? 'store_corporate_reinhardt' 
        : (matchedLocation ? matchedLocation.id : 'store_corporate_reinhardt');

      if (onboardingMode === 'vault') {
        const provisionVaultWorkerFn = httpsCallable(functions, 'provisionVaultWorker');
        
        const result = await provisionVaultWorkerFn({
          tenantId: tenantId,
          workerType: 'contractor',
          systemRole: formData.systemRole, 
          homeStoreId: derivedHomeStoreId, 
          firstName: formData.firstName,
          lastName: formData.lastName,
          corporateEmail: formData.corporateEmail,
          personalEmail: formData.personalEmail,
          phone: formData.phone,
          operatingCity: formData.operatingCity,
          operatingState: formData.operatingState,
          assignedTenant: formData.assignedTenant,
          commissionTier: formData.commissionTier,
          offlineDocs: {
            w9: formData.offlineW9,
            ica: formData.offlineICA,
            nda: formData.offlineNDA,
            techLicense: formData.offlineTechLicense,
            autonomy: formData.offlineAutonomy,
            azDibs: formData.operatingState.toUpperCase() === 'AZ' ? formData.offlineAzDibs : false
          }
        });

        if (result.data.success) {
          alert("Contractor successfully minted! Secure FTL Vault link dispatched.");
          onClose();
        } else {
          throw new Error(result.data.message || "Unknown provisioning error.");
        }

      } else {
        const newContractor = await createTenantContractor(tenantId, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          corporateEmail: formData.corporateEmail,
          personalEmail: formData.personalEmail,
          email: formData.corporateEmail, 
          phone: formData.phone,
          operatingCity: formData.operatingCity,
          operatingState: formData.operatingState,
          assignedTenant: formData.assignedTenant,
          homeStoreId: derivedHomeStoreId, 
          commissionTier: formData.commissionTier,
          systemRole: formData.systemRole,
          status: 'pre_launch',
          complianceStatus: 'pending_checkhq',
          onboardingMethod: 'checkhq_invite',
          checkhq_id: null
        });

        const generateOnboardLinkFn = httpsCallable(functions, 'generateCheckOnboardLink');
        await generateOnboardLinkFn({ 
          tenantId, 
          workerId: newContractor.id, 
          workerType: 'contractor',
          email: formData.personalEmail, 
          firstName: formData.firstName,
          lastName: formData.lastName
        });

        alert("CheckHQ secure onboarding link generated and dispatched!");
        onClose();
      }
    } catch (error) {
      console.error("Failed to provision contractor:", error);
      alert(`Provisioning Failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-2xl bg-slate-50 h-full border-l border-slate-300 shadow-2xl flex flex-col slide-in-from-right">
        
        {/* Header */}
        <div className="bg-slate-900 p-6 border-b border-slate-700 flex justify-between items-center shrink-0">
          <div>
            <h2 className="text-xl font-black text-white uppercase tracking-tight">1099 Contractor Onboarding</h2>
            <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mt-1">Personnel Provisioning Record</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>

        {/* Onboarding Mode Selector */}
        <div className="bg-white border-b border-slate-200 p-4 shrink-0 flex gap-4">
          <button 
            type="button"
            onClick={() => setOnboardingMode('vault')}
            className={`flex-1 py-3 px-4 text-left border transition-all ${
              onboardingMode === 'vault' 
                ? 'border-emerald-500 bg-emerald-50/50 shadow-sm' 
                : 'border-slate-200 hover:border-slate-300 bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-black text-slate-900 uppercase tracking-wider">eeKoSystem Vault</span>
              <span className="text-[8px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 uppercase tracking-widest">In-House FTL</span>
            </div>
            <p className="text-[9px] text-slate-500 mt-1">Contractor completes W-9 & ACH via internal First Time Login.</p>
          </button>

          <button 
            type="button"
            onClick={() => setOnboardingMode('checkhq')}
            className={`flex-1 py-3 px-4 text-left border transition-all ${
              onboardingMode === 'checkhq' 
                ? 'border-emerald-500 bg-emerald-50/50 shadow-sm' 
                : 'border-slate-200 hover:border-slate-300 bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-black text-slate-900 uppercase tracking-wider">CheckHQ Invite</span>
              <span className="text-[8px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 uppercase tracking-widest">API Flow</span>
            </div>
            <p className="text-[9px] text-slate-500 mt-1">Dispatches CheckHQ link for external compliance & payroll setup.</p>
          </button>
        </div>

        {/* High-Density Lightweight Form */}
        <form onSubmit={handleSubmit} id="contractor-form" className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          
          {/* Section 1: Contractor Identity */}
          <div className="bg-white border border-slate-200 p-5 shadow-sm rounded-sm">
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-2 mb-4">Identity & Contact</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">First Name <span className="text-red-500">*</span></label>
                <input required type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Last Name <span className="text-red-500">*</span></label>
                <input required type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Corporate Email (Login) <span className="text-red-500">*</span></label>
                <input required type="email" name="corporateEmail" value={formData.corporateEmail} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Personal Email (Dispatch) <span className="text-red-500">*</span></label>
                <input required type="email" name="personalEmail" value={formData.personalEmail} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Mobile Phone <span className="text-red-500">*</span></label>
                <input required type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
            </div>
          </div>

          {/* Section 2: Operational Assignment */}
          <div className="bg-white border border-slate-200 p-5 shadow-sm rounded-sm">
            <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100 pb-2 mb-4">Operational Assignment</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Operating Assignment <span className="text-red-500">*</span></label>
                <select 
                  required 
                  name="assignedTenant" 
                  value={formData.assignedTenant} 
                  onChange={handleChange} 
                  disabled={loadingLocations}
                  className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-sm disabled:opacity-50"
                >
                  <option value="">{loadingLocations ? 'Fetching locations...' : 'Select Assignment...'}</option>
                  
                  {/* DYNAMIC DATABASE LOCATIONS */}
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.storeName || loc.name}>
                      {loc.storeName || loc.name} {loc.city ? `(${loc.city})` : ''}
                    </option>
                  ))}
                  
                  {/* FIELD SALES FALLBACK */}
                  <option value="Remote / Field Sales">Remote / Field Sales</option>
                </select>
              </div>
              
              <div className="col-span-2">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">System Role & Clearance <span className="text-red-500">*</span></label>
                <select required name="systemRole" value={formData.systemRole} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-sm">
                  <option value="ASSOCIATE">Field Rep / Associate (Standard Access)</option>
                  <option value="STORE_MANAGER">Store Manager (Location Override)</option>
                  <option value="REGIONAL_DIRECTOR">Regional Director (Multi-Location Override)</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Operating City <span className="text-red-500">*</span></label>
                <input required type="text" name="operatingCity" value={formData.operatingCity} onChange={handleChange} className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none rounded-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Operating State <span className="text-red-500">*</span></label>
                <input required type="text" name="operatingState" value={formData.operatingState} onChange={handleChange} maxLength="2" placeholder="WA" className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none uppercase rounded-sm" />
              </div>
              
              {/* ⚡️ NEW: DYNAMIC COMMISSION TIERS ⚡️ */}
              <div className="col-span-2">
                <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1 flex items-center justify-between">
                  <span>Commission Tier <span className="text-red-500">*</span></span>
                  {loadingTiers && <span className="text-[9px] text-emerald-500 animate-pulse">Syncing Matrix...</span>}
                </label>
                <select 
                  required 
                  name="commissionTier" 
                  value={formData.commissionTier} 
                  onChange={handleChange} 
                  disabled={loadingTiers}
                  className="w-full bg-slate-50 border border-slate-300 px-3 py-2 text-sm font-bold text-slate-800 focus:border-emerald-500 focus:outline-none cursor-pointer rounded-sm disabled:opacity-50"
                >
                  <option value="" disabled>Select Commission Array...</option>
                  {commissionTiers.map(tier => (
                    <option key={tier.id} value={tier.targetTier}>
                      {tier.name} ({tier.targetTier})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Section 3: Offline Document Verification (Vault Mode Only) */}
          {onboardingMode === 'vault' && (
            <div className="bg-white border border-emerald-200 p-5 shadow-sm rounded-sm">
              <h3 className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest border-b border-emerald-100 pb-2 mb-4">Offline Document Verification</h3>
              <p className="text-[10px] text-slate-500 mb-4 leading-relaxed">If you have already executed paper or third-party documents with this contractor, check the boxes below. The system will <strong>bypass</strong> these requirements during their FTL setup.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 border border-slate-200 rounded-sm hover:bg-slate-100">
                  <input type="checkbox" name="offlineW9" checked={formData.offlineW9} onChange={handleChange} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 border-slate-300" />
                  <div>
                    <span className="text-xs font-black text-slate-800 uppercase tracking-widest">W-9 Tax Form</span>
                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Already collected</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 border border-slate-200 rounded-sm hover:bg-slate-100">
                  <input type="checkbox" name="offlineICA" checked={formData.offlineICA} onChange={handleChange} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 border-slate-300" />
                  <div>
                    <span className="text-xs font-black text-slate-800 uppercase tracking-widest">ICA Contract</span>
                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Already signed</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 border border-slate-200 rounded-sm hover:bg-slate-100">
                  <input type="checkbox" name="offlineNDA" checked={formData.offlineNDA} onChange={handleChange} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 border-slate-300" />
                  <div>
                    <span className="text-xs font-black text-slate-800 uppercase tracking-widest">NDA Contract</span>
                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Already signed</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 border border-slate-200 rounded-sm hover:bg-slate-100">
                  <input type="checkbox" name="offlineTechLicense" checked={formData.offlineTechLicense} onChange={handleChange} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 border-slate-300" />
                  <div>
                    <span className="text-xs font-black text-slate-800 uppercase tracking-widest">Tech License</span>
                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Already acknowledged</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 border border-slate-200 rounded-sm hover:bg-slate-100">
                  <input type="checkbox" name="offlineAutonomy" checked={formData.offlineAutonomy} onChange={handleChange} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 border-slate-300" />
                  <div>
                    <span className="text-xs font-black text-slate-800 uppercase tracking-widest">Autonomy Clause</span>
                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Already acknowledged</p>
                  </div>
                </label>
                {formData.operatingState.toUpperCase() === 'AZ' && (
                  <label className="flex items-center gap-3 cursor-pointer p-3 bg-amber-50 border border-amber-200 rounded-sm hover:bg-amber-100">
                    <input type="checkbox" name="offlineAzDibs" checked={formData.offlineAzDibs} onChange={handleChange} className="w-4 h-4 text-amber-600 focus:ring-amber-500 border-amber-300" />
                    <div>
                      <span className="text-xs font-black text-amber-900 uppercase tracking-widest">AZ DIBS Form</span>
                      <p className="text-[9px] text-amber-700 font-bold uppercase tracking-widest">Already collected</p>
                    </div>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Vault / API Explainer */}
          {onboardingMode === 'vault' ? (
            <div className="bg-slate-100 border border-slate-300 p-5 rounded-sm flex items-start gap-4">
              <div className="w-10 h-10 bg-white border border-slate-300 text-slate-600 rounded-full flex items-center justify-center shrink-0">
                <i className="fa-solid fa-vault"></i>
              </div>
              <div>
                <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Compliance Handoff (FTL Lock)</h4>
                <p className="text-[10px] font-bold text-slate-500 mt-1 leading-relaxed">
                  Upon dispatch, this contractor's CRM profile will be created and locked in a <strong>pre_launch</strong> status. They will receive an email prompting their First Time Login (FTL), where they must securely input their W-9 data, ACH routing, and sign the ICA/NDA before accessing the system.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 p-5 rounded-sm flex items-start gap-4">
              <div className="w-10 h-10 bg-white border border-amber-200 text-amber-600 rounded-full flex items-center justify-center shrink-0">
                <i className="fa-solid fa-envelope-open-text"></i>
              </div>
              <div>
                <h4 className="text-xs font-black text-amber-900 uppercase tracking-widest">External API Handoff</h4>
                <p className="text-[10px] font-bold text-amber-700 mt-1 leading-relaxed">
                  This routes the contractor entirely through CheckHQ. They will receive an automated email from CheckHQ to establish their tax profile and direct deposit settings. Their CRM status will read <strong>pending_checkhq</strong> until the API confirms clearance.
                </p>
              </div>
            </div>
          )}

        </form>

        {/* Footer Actions */}
        <div className="p-4 border-t border-slate-300 bg-white flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-6 py-2 text-[10px] font-bold text-slate-600 border border-slate-300 hover:bg-slate-50 uppercase tracking-widest transition-colors rounded-sm">
            Cancel
          </button>
          <button 
            type="submit"
            form="contractor-form"
            disabled={isSubmitting || tenantLoading || loadingLocations || loadingTiers}
            className="px-6 py-2 text-[10px] font-bold text-white bg-slate-900 border border-slate-900 hover:bg-slate-800 uppercase tracking-widest transition-colors shadow-sm disabled:opacity-50 rounded-sm flex items-center gap-2"
          >
            {tenantLoading ? 'Authenticating...' : (isSubmitting ? <><i className="fa-solid fa-spinner fa-spin"></i> Vaulting...</> : (onboardingMode === 'vault' ? <><i className="fa-solid fa-paper-plane"></i> Dispatch FTL Link</> : <><i className="fa-solid fa-paper-plane"></i> Dispatch CheckHQ Invite</>))}
          </button>
        </div>

      </div>
    </div>
  );
}